import { useState, type ReactNode } from "react";
import { Lock } from "lucide-react";
import { useAuthOptional } from "@/auth/AuthProvider";
import { Boton } from "@/components/ui";
import { useToast } from "@/components/ui/Toast";
import { api, ApiError } from "@/lib/api";
import { CASILLA_SENSIBLES, PRIVACY_VERSION } from "@/legal/aviso";
import { useDatosApi } from "../DatosContext";

/**
 * Mi identidad, Diagnóstico y Rueda 360 preguntan salud, bienestar emocional, fe y vida familiar: datos sensibles.
 * El aviso pide consentimiento expreso y por escrito justo antes, opcional y sin bloquear lo demás. Sin él, la sección
 * muestra la casilla en vez de las preguntas. En modo ejemplo no aplica (no se guarda nada).
 */
export function CandadoSensibles({ children }: { children: ReactNode }) {
  const auth = useAuthOptional();
  const { modo } = useDatosApi();
  const toast = useToast();
  const [acepto, setAcepto] = useState(false);
  const [cargando, setCargando] = useState(false);
  if (modo === "demo" || !auth || auth.sensibles) return <>{children}</>;

  async function autorizar() {
    setCargando(true);
    try {
      await api("/api/me/sensibles", { method: "POST", json: { acepto: true, version: auth!.privacyVersion ?? PRIVACY_VERSION } });
      await auth!.recargar();
    } catch (e) { toast.error(e instanceof ApiError ? e.message : "No pude guardar tu autorización. Intenta de nuevo."); }
    setCargando(false);
  }

  return (
    <section className="flex flex-col gap-4 rounded-md border border-borde bg-white p-4">
      <p className="flex items-start gap-2 text-[15px]"><Lock className="mt-0.5 h-4 w-4 shrink-0 text-tinta-suave" aria-hidden /><span>Estas preguntas tocan temas personales (salud, bienestar emocional, creencias y vida familiar), que la ley trata como <b>datos sensibles</b>. Contestarlas es opcional: sin ellas puedes usar todo lo demás de la app.</span></p>
      <label className="flex items-start gap-3 rounded-md border border-borde p-4">
        <input type="checkbox" className="mt-1 h-5 w-5 shrink-0" checked={acepto} onChange={(e) => setAcepto(e.target.checked)} />
        <span className="text-[15px]">{CASILLA_SENSIBLES}</span>
      </label>
      <p className="text-sm text-tinta-suave">Detalle en el <a className="underline" href="/aviso-de-privacidad" target="_blank" rel="noreferrer">aviso de privacidad</a>. Puedes revocarla en Cuenta.</p>
      <Boton disabled={!acepto} cargando={cargando} onClick={() => void autorizar()}>Autorizar y contestar</Boton>
    </section>
  );
}
